import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Button,
    TextField,
    InputAdornment,
    Avatar,
    Chip,
    Fade,
    Card,
    useTheme
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AssessmentIcon from '@mui/icons-material/Assessment';
import FilterListIcon from '@mui/icons-material/FilterList';
import PersonIcon from '@mui/icons-material/Person';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { User } from '../../types/auth';

const LearnerList = () => {
    const [learners, setLearners] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();
    const theme = useTheme();

    useEffect(() => {
        fetchLearners();
    }, []);

    const fetchLearners = async () => {
        try {
            const res = await api.get('/api/users');
            // Filter only learners
            const learnerList = res.data.filter((u: User) => u.role === 'learner');
            setLearners(learnerList);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching learners", err);
            setLoading(false);
        }
    };

    const filteredLearners = learners.filter(learner =>
        learner.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        learner.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        learner.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) return <div>Chargement...</div>;

    return (
        <Box sx={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%)',
            p: { xs: 2, md: 4 }
        }}>
            {/* Hero Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 4,
                    p: { xs: 3, md: 4 },
                    mb: 4,
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: '0 10px 40px -10px rgba(102, 126, 234, 0.5)'
                }}
            >
                <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                <Box sx={{ position: 'absolute', bottom: -30, right: 100, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, position: 'relative', zIndex: 1 }}>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 64, height: 64, backdropFilter: 'blur(10px)' }}>
                        <AssessmentIcon sx={{ fontSize: 32 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: 'Quicksand, sans-serif' }}>
                            Rapports Apprenants
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.9 }}>
                            Générez et envoyez les rapports de progression
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            {/* Filters & Content */}
            <Fade in timeout={500}>
                <Card sx={{ borderRadius: 4, overflow: 'visible', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', bgcolor: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(10px)' }}>
                    <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Rechercher un apprenant par nom ou email..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 3,
                                    bgcolor: 'white',
                                    '& fieldset': { borderColor: 'rgba(0,0,0,0.1)' },
                                    '&:hover fieldset': { borderColor: '#667eea' },
                                    '&.Mui-focused fieldset': { borderColor: '#667eea' },
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: 'text.secondary' }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <IconButton sx={{ bgcolor: 'white', borderRadius: 2, border: '1px solid rgba(0,0,0,0.1)' }}>
                            <FilterListIcon />
                        </IconButton>
                    </Box>

                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'rgba(249, 250, 251, 0.5)' }}>
                                    <TableCell sx={{ py: 2, fontWeight: 600, color: 'text.secondary', pl: 4 }}>Apprenant</TableCell>
                                    <TableCell sx={{ py: 2, fontWeight: 600, color: 'text.secondary', display: { xs: 'none', md: 'table-cell' } }}>Email</TableCell>
                                    <TableCell sx={{ py: 2, fontWeight: 600, color: 'text.secondary', display: { xs: 'none', sm: 'table-cell' } }}>Date d'inscription</TableCell>
                                    <TableCell align="right" sx={{ py: 2, fontWeight: 600, color: 'text.secondary', pr: 4 }}>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredLearners.map((learner) => (
                                    <TableRow
                                        key={learner._id}
                                        hover
                                        sx={{
                                            transition: 'all 0.2s',
                                            '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.04)' }
                                        }}
                                    >
                                        <TableCell sx={{ pl: 4 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Avatar sx={{ bgcolor: '#e0e7ff', color: '#667eea', fontWeight: 'bold' }}>
                                                    {learner.firstName[0]}{learner.lastName[0]}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                        {learner.firstName} {learner.lastName}
                                                    </Typography>
                                                    {/* Show email on mobile */}
                                                    <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'block', md: 'none' }, fontSize: '0.75rem' }}>
                                                        {learner.email}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {learner.email}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                            <Chip
                                                label={new Date(learner.createdAt).toLocaleDateString()}
                                                size="small"
                                                sx={{ bgcolor: 'rgba(0,0,0,0.05)', fontWeight: 500 }}
                                            />
                                        </TableCell>
                                        <TableCell align="right" sx={{ pr: 4 }}>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                endIcon={<ChevronRightIcon />}
                                                onClick={() => navigate(`/reports/${learner._id}`)}
                                                sx={{
                                                    borderRadius: 2,
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                    boxShadow: '0 4px 10px rgba(102, 126, 234, 0.3)',
                                                    '&:hover': {
                                                        boxShadow: '0 6px 15px rgba(102, 126, 234, 0.5)',
                                                    }
                                                }}
                                            >
                                                Générer
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {filteredLearners.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, opacity: 0.5 }}>
                                                <PersonIcon sx={{ fontSize: 48 }} />
                                                <Typography variant="body1">
                                                    Aucun apprenant trouvé pour cette recherche.
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            </Fade>
        </Box>
    );
};

export default LearnerList;
